// Backend key formats for ratio metrics, which divide one metric by another
// (e.g. "Shakes per Order") so leaderboards stay fair across subjects with
// very different volumes:
//   sales_driver_ratio:<numerator_driver_id>:<denominator_driver_id>
//   sales_group_ratio:<numerator_group_uuid>:<denominator_driver_id>
// plus "store_"-prefixed versions for store-vs-store contests, where each
// store also races its own per-store target. The form composes the key locally
// from two plain picks, so get_available_metrics needs no ratio entries (a
// pair enumeration would be quadratic).
const flavors = (isStoreVsStore) =>
  isStoreVsStore
    ? {
        driver: "store_sales_driver:",
        group: "store_sales_group:",
        driverRatio: "store_sales_driver_ratio:",
        groupRatio: "store_sales_group_ratio:",
      }
    : {
        driver: "sales_driver:",
        group: "sales_group:",
        driverRatio: "sales_driver_ratio:",
        groupRatio: "sales_group_ratio:",
      };

const hasPrefix = (metric, prefix) =>
  typeof metric?.value === "string" && metric.value.startsWith(prefix);

// The metrics a ratio can be built from. A sales group can only be the
// numerator: denominators (like Orders) are always sales drivers.
export function ratioBuildingBlocks(metrics, isStoreVsStore) {
  const { driver, group } = flavors(isStoreVsStore);
  const denominators = metrics.filter((m) => hasPrefix(m, driver));
  const numerators = metrics.filter(
    (m) => hasPrefix(m, driver) || hasPrefix(m, group)
  );
  return { numerators, denominators };
}

// Whether a ratio of two different metrics can be built from these options.
export function canBuildRatio({ numerators, denominators }) {
  return (
    denominators.length > 0 &&
    numerators.some((n) => denominators.some((d) => d.value !== n.value))
  );
}

// The key for two dropdown values, or "" until both sides are picked, so the
// form's generic "Pick a metric" validation keeps working.
export function composeRatioKey(num, den, isStoreVsStore) {
  if (!num || !den) return "";
  const { driver, group, driverRatio, groupRatio } = flavors(isStoreVsStore);
  const denId = den.slice(driver.length);
  if (num.startsWith(group)) {
    return `${groupRatio}${num.slice(group.length)}:${denId}`;
  }
  return `${driverRatio}${num.slice(driver.length)}:${denId}`;
}

// Parse a saved ratio key back into the two dropdown values for edit-mode
// prefill. Null for non-ratio or malformed keys.
export function parseRatioKey(metricKey) {
  if (typeof metricKey !== "string") return null;
  for (const isStoreVsStore of [true, false]) {
    const { driver, group, driverRatio, groupRatio } = flavors(isStoreVsStore);
    for (const [prefix, numPrefix] of [
      [driverRatio, driver],
      [groupRatio, group],
    ]) {
      if (metricKey.startsWith(prefix)) {
        const parts = metricKey.slice(prefix.length).split(":");
        if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
        return { num: `${numPrefix}${parts[0]}`, den: `${driver}${parts[1]}` };
      }
    }
  }
  return null;
}

const isSalesGroupValue = (value) =>
  typeof value === "string" &&
  (value.startsWith("sales_group:") ||
    value.startsWith("store_sales_group:") ||
    value.startsWith("store_sales_group_goal:"));

// The catalog leaves out archived sales groups, but a contest already using
// one keeps working, so when editing it add that group back. Otherwise the
// picker would look empty while the form still held the saved key. Uses the
// labels the contest was serialized with. A missing metric has no label and
// isn't added, so it still shows as unset.
export function withSavedSalesGroup(catalog, contest) {
  const key = contest?.metric_key;
  const picks = parseRatioKey(key);
  const saved = picks
    ? {
        value: picks.num,
        label: contest.metric_numerator_label,
        format: "number",
        direction: "higher_is_better",
      }
    : {
        value: key,
        label: contest?.metric_label,
        format: contest?.metric_format,
        direction: contest?.metric_direction,
        requires_store_targets: contest?.requires_store_targets,
      };
  if (
    !isSalesGroupValue(saved.value) ||
    !saved.label ||
    catalog.some((m) => m.value === saved.value)
  ) {
    return catalog;
  }
  return [...catalog, saved];
}
